import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

// project imports
import AuthWrapper1 from './AuthWrapper1';
import AuthCardWrapper from './AuthCardWrapper';
import ViewOnlyAlert from './ViewOnlyAlert';
import LoginProvider from './LoginProvider';

import Logo from 'ui-component/Logo';
import AuthFooter from 'ui-component/cards/AuthFooter';
import AnimateButton from 'ui-component/extended/AnimateButton';

import useAuth from 'hooks/useAuth';
import { APP_AUTH } from 'config';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import { useIntl } from 'react-intl';
import WelcomeBanner from './WelcomeBanner';
import SignupLogo from 'ui-component/SignupLogo';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { getCurrentUser, getSession, startAttributeVerification, updateAttributes } from '../../../utils/CognitoCustomChallengeService';
import { IconButton } from '@mui/material';

const userPool = new CognitoUserPool({
  UserPoolId: process.env.awsPoolId,
  ClientId: process.env.clientId
});

// A mapping of auth types to dynamic imports
const authCodeVerificationImports = {
  firebase: () => import('./firebase/AuthCodeVerification'),
  jwt: () => import('./jwt/AuthCodeVerification'),
  aws: () => import('./aws/AuthCodeVerification'),
  auth0: () => import('./auth0/AuthCodeVerification'),
  supabase: () => import('./supabase/AuthCodeVerification')
};

// ===========================|| AUTH3 - CODE VERIFICATION ||=========================== //

export default function Enable2FA() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const [AuthCodeVerificationComponent, setAuthCodeVerificationComponent] = useState(null);
  const { isLoggedIn } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [isResending, setIsResending] = useState(false);
  const intl = useIntl();
  const user = getCurrentUser();

  const [searchParams] = useSearchParams();
  const authParam = searchParams.get('auth') || '';

  useEffect(() => {
    // Get email from localStorage to display on the page
    const email = localStorage.getItem('email') || '';
    setUserEmail(email);
  }, []);

  useEffect(() => {
    const selectedAuth = authParam || APP_AUTH;

    const importAuthCodeVerificationComponent = authCodeVerificationImports[selectedAuth];

    importAuthCodeVerificationComponent()
      .then((module) => setAuthCodeVerificationComponent(() => module.default))
      .catch((error) => {
        console.error(`Error loading ${selectedAuth} AuthCodeVerification`, error);
      });
  }, [authParam]);

  const handleConfirm2FA = async () => {
    if (!user) {
      dispatch(
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'sessionExpired' }) || 'Session expired. Please log in again.',
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      navigate('/landingPage');
      return;
    }
    setIsResending(true);
    try {
      await getSession(user);
      await startAttributeVerification(user, 'email'); // or 'phone_number'
      navigate('/twoFactorMethodSelection');
    } catch (error) {
      console.error('Error in handleConfirm2FA:', error);
      setIsResending(false);
      dispatch(
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'sessionExpired' }) || 'Session expired. Please log in again.',
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      navigate('/landingPage');
    }
  };

  const handleNoThankyou = async () => {
    if (!user) {
      dispatch(
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'sessionExpired' }) || 'Session expired. Please log in again.',
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      navigate('/landingPage');
      return;
    }
    setIsResending(true);
    try {
      await getSession(user);
      const now = Math.floor(Date.now() / 1000).toString();
      await updateAttributes(user, {
        'custom:mfa_reminder': 'true',
        'custom:mfa_denied_timestamp': now
        // 'custom:mfa_denied_timestamp': ''
      });
      navigate('/');
    } catch (error) {
      console.error('Error in handleNoThankyou:', error);
      setIsResending(false);
      dispatch(
        openSnackbar({
          open: true,
          message: intl.formatMessage({ id: 'sessionExpired' }) || 'Session expired. Please log in again.',
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: false
        })
      );
      navigate('/landingPage');
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <AuthWrapper1>
      <Grid container sx={{ m: { xs: 2, md: 0 }, minHeight: 'calc(100vh - 0px)' }}>
        <Grid size={{ xs: 12, md: 12, }}>
          <Grid size={12} sx={{ my: { xs: 1, md: 4 }, pl: { xs: 0, md: 6 }, textAlign: { xs: 'center', md: 'left' } }}>
            <SignupLogo /> 
          </Grid>
          <Grid container sx={{ height: 'calc(100% - 250px)' }}>
            <Grid container spacing={.5} sx={{
              maxWidth: '360px',
              m: '0 auto 0 auto',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Stack spacing={3.6} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                {/* Back arrow and title */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, width: '100%', justifyContent: 'center', position: 'relative' }}>
                  <IconButton onClick={handleBack} sx={{ mr: 1, color: 'grey.900', position:'absolute', top:{xs:-4, md:4},  left:{xs:'0px', md:'-12%'} }}>
                    <KeyboardBackspaceIcon />
                  </IconButton>
                  <Typography variant={'h2'} fontSize={downMD ? '20px' : '34px'} sx={{ color: '#000', textAlign: 'center', }}>
                    {intl.formatMessage({ id: 'enable2FATitle' })}
                  </Typography>
                </Box>
                <Box sx={{width:'100%'}}>
                  <Typography variant="body2" color='#78909C' sx={{ textAlign: 'center', lineHeight:'24px' }}>
                    {intl.formatMessage({ id: 'enable2FAMessage' })}
                  </Typography>
                </Box>
                <Box sx={{width:'100%'}}>
                  <Button
                    variant="contained"
                    onClick={handleConfirm2FA}
                    disableElevation
                    fullWidth
                    disabled={isResending}
                  >
                    {intl.formatMessage({ id: 'enable2FAConfirm' })}
                  </Button>
                </Box>
                 <Box sx={{width:'100%'}}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleNoThankyou}
                    disableElevation
                    disabled={isResending}
                  >
                    {intl.formatMessage({ id: 'noThankyou' })}
                  </Button>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        
      </Grid>
    </AuthWrapper1>
  );
}
